import { request } from './client'

/* **W-129 — THE HOTEL'S OWN WORDS.** Sprint 55, for CQ-130.

   The client's answer on 18 September was that the invented Arabic stays and the hotel
   corrects it from the settings during go-live. This is those settings.

   **IT IS NOT ALL OF W-129.** Pack 5 draws shifts, inspection sampling, quiet hours and
   occupancy sync on the same screen; four of those govern features this product does not
   have, and one is TQ-01. A row that sets nothing teaches an administrator that the screen
   does not work, and then they stop trusting the row that does. */

export interface ConfigurationRole {
  roleId: string
  /** `FLOOR_SUPERVISOR`. Never changes, and never translated — it is what the product
   *  runs on, and the name beside it is what people read. */
  code: string
  name: string
  nameAr: string | null
  /** A role the product ships. Renameable — that is the point — and not deletable. */
  isSystem: boolean
  /** How many people hold it in THIS hotel. */
  peopleHolding: number
}

export interface ConfigurationView {
  propertyId: string
  /** NJF, KBL. Shown and not offered — see the server's `ConfigurationView`. */
  code: string
  name: string
  nameAr: string | null
  timeZone: string
  defaultLanguage: string
  /** **WHETHER A CLEANED ROOM WAITS FOR SOMEBODY TO LOOK AT IT.** Enforced since Sprint 1
   *  by the readiness rule and by task submission; settable from nowhere until Sprint 77,
   *  when walking the UAT prerequisites against the build found CF-04 unmeetable. */
  requireInspection: boolean
  /** The zones this SERVER can resolve, so the screen offers a list rather than a box. */
  timeZones: string[]
  roles: ConfigurationRole[]
}

export const fetchConfiguration = (
  propertyId: string,
  options: { signal?: AbortSignal } = {},
) => request<ConfigurationView>(
  `/properties/${propertyId}/configuration`,
  options.signal ? { signal: options.signal } : {},
)

/**
 * The hotel's own details. **An empty Arabic name is a real answer** and clears the column
 * — a hotel called by a transliteration in both languages is saying the English name is the
 * name.
 */
export const saveHotel = (
  propertyId: string,
  body: {
    name: string; nameAr: string; timeZone: string; defaultLanguage: string
    /** Omitted means LEAVE IT ALONE. The server treats null that way deliberately, so a
     *  client that has never heard of the field cannot switch a hotel's policy off. */
    requireInspection?: boolean
  },
) => request<ConfigurationView>(`/properties/${propertyId}/configuration/hotel`, {
  method: 'PUT',
  body,
})

/**
 * One role's name. **Its code is never sent and never changes** — a hotel renaming a role
 * is changing a label, not re-pointing the product at a new role.
 *
 * Its own call rather than a field on the save above, because they are two different acts
 * with two different rows in the activity log: a single "save configuration" covering both
 * would record that somebody changed something.
 */
export const saveRoleName = (
  propertyId: string,
  roleId: string,
  body: { name: string; nameAr: string },
) => request<ConfigurationView>(
  `/properties/${propertyId}/configuration/roles/${roleId}`,
  { method: 'PUT', body },
)

/* ── W-129's LAST CARD · WHEN THIS HOTEL WORKS (Sprint 80) ─────────────────────

   Two shifts were seeded and a hotel had no way to say anything else. And the hours are
   not a label: **a shift's length IS the credit budget of every lane on the assignment
   board** — see `ShiftRules` and defect #125. */

export interface ShiftRow {
  shiftId: string
  code: string
  name: string
  nameAr: string | null
  /** Minutes past local midnight, 0–1439. Not a time string: a shift is a position in
   *  the hotel's own day, and the hotel's day is what its time zone says. */
  startMinuteLocal: number
  /** **May be earlier than the start** — that is a night shift, and the wrap is read
   *  server-side rather than by each client inventing the same arithmetic. */
  endMinuteLocal: number
  /** Derived, never stored. */
  lengthMinutes: number
  /** What a lane on the board is measured against. The number this card actually moves. */
  creditBudget: number
  isActive: boolean
  rosteredEver: number
}

export const fetchShifts = (
  propertyId: string, options: { signal?: AbortSignal } = {},
) => request<ShiftRow[]>(
  `/properties/${propertyId}/shifts`,
  options.signal ? { signal: options.signal } : {})

export const addShift = (
  propertyId: string,
  body: {
    code: string; name: string; nameAr?: string | null
    startMinuteLocal: number; endMinuteLocal: number
  },
) => request<ShiftRow[]>(`/properties/${propertyId}/shifts`, { method: 'POST', body })

export const saveShift = (
  propertyId: string, id: string,
  body: {
    name: string; nameAr?: string | null
    startMinuteLocal: number; endMinuteLocal: number
  },
) => request<ShiftRow[]>(`/properties/${propertyId}/shifts/${id}`, { method: 'PUT', body })

export const setShiftActive = (
  propertyId: string, id: string, isActive: boolean,
) => request<ShiftRow[]>(
  `/properties/${propertyId}/shifts/${id}/active`, { method: 'PUT', body: { isActive } })
